import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import './DraftPages.css'

const Icons = {
  shield: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
  lock: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  ),
  eye: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ),
  database: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  globe: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  )
}

const dataCategories = [
  {
    icon: Icons.users,
    title: 'Account Information',
    items: [
      'Name and email address',
      'Company or organization name',
      'Billing and payment information',
      'Phone number (optional)'
    ]
  },
  {
    icon: Icons.database,
    title: 'Solar Plant Data',
    items: [
      'Plant location and configuration',
      'Energy production metrics',
      'Equipment performance data',
      'Sensor readings and telemetry'
    ]
  },
  {
    icon: Icons.eye,
    title: 'Usage Information',
    items: [
      'Platform interaction data',
      'Feature usage analytics',
      'Login and session information',
      'Device and browser data'
    ]
  }
]

const userRights = [
  { right: 'Access', description: 'Request a copy of your personal data' },
  { right: 'Rectification', description: 'Correct inaccurate or incomplete data' },
  { right: 'Erasure', description: 'Request deletion of your personal data' },
  { right: 'Portability', description: 'Receive your data in a portable format' },
  { right: 'Restriction', description: 'Limit how we process your data' },
  { right: 'Objection', description: 'Object to certain types of processing' }
]

const securityMeasures = [
  'Encryption for all data in transit (TLS 1.2+)',
  'Encryption for data at rest',
  'SOC 2 Type II certified infrastructure (Render)',
  'Multi-factor authentication support',
  'Role-based access controls',
  'Regular security updates and monitoring'
]

export default function Privacy() {
  const lastUpdated = 'January 28, 2025'

  return (
    <div className="draft-page privacy-page">
      {/* Hero */}
      <section className="draft-hero">
        <div className="container">
          <ScrollReveal>
            <span className="draft-badge">Legal</span>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h1 className="draft-title">
              Privacy{' '}
              <GradientText gradient="energy">Policy</GradientText>
            </h1>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="draft-subtitle">
              At AI4Renewables, we take your privacy seriously. This policy explains how we collect,
              use, and protect your personal information when you use the AI4Solar platform.
            </p>
          </ScrollReveal>
          <ScrollReveal delay={0.3}>
            <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem', marginTop: 'var(--space-md)' }}>
              Last updated: {lastUpdated}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Overview */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Our Commitment to Privacy</h2>
            <p>Transparency, security, and control over your data</p>
          </ScrollReveal>

          <div className="values-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }}>
            <ScrollReveal delay={0.1}>
              <motion.div className="value-card" whileHover={{ y: -5 }}>
                <span className="value-icon">{Icons.shield}</span>
                <h3>GDPR Compliant</h3>
                <p>We comply with the General Data Protection Regulation and other applicable data protection laws.</p>
              </motion.div>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <motion.div className="value-card" whileHover={{ y: -5 }}>
                <span className="value-icon">{Icons.lock}</span>
                <h3>Data Security</h3>
                <p>Enterprise-grade encryption and security measures protect your data at every stage.</p>
              </motion.div>
            </ScrollReveal>
            <ScrollReveal delay={0.3}>
              <motion.div className="value-card" whileHover={{ y: -5 }}>
                <span className="value-icon">{Icons.globe}</span>
                <h3>Secure Cloud Infrastructure</h3>
                <p>Your data is hosted on SOC 2 Type II certified infrastructure with enterprise-grade security.</p>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Information We Collect */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Information We Collect</h2>
            <p>Types of data we gather to provide our services</p>
          </ScrollReveal>

          <div className="values-grid">
            {dataCategories.map((category, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <motion.div className="value-card docs-card" whileHover={{ y: -5 }}>
                  <span className="value-icon">{category.icon}</span>
                  <h3>{category.title}</h3>
                  <ul className="docs-links" style={{ marginTop: 'var(--space-md)' }}>
                    {category.items.map((item, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                        <span style={{ color: 'var(--color-energy)', width: '16px', height: '16px', flexShrink: 0 }}>
                          {Icons.check}
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How We Use Information */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>How We Use Your Information</h2>
            <p>The purposes for which we process your data</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Service Delivery</h3>
                <p>
                  We use your information to provide, maintain, and improve the AI4Solar platform, including
                  real-time monitoring, predictive maintenance alerts, and energy production analytics.
                </p>
              </div>

              <div className="legal-section">
                <h3>Communication</h3>
                <p>
                  We may contact you about service updates, security alerts, administrative messages,
                  and—with your consent—marketing communications about new features and offerings.
                </p>
              </div>

              <div className="legal-section">
                <h3>Analytics and Improvement</h3>
                <p>
                  We analyze aggregated and anonymized usage data to understand how our platform is used,
                  identify areas for improvement, and develop new features that benefit all users.
                </p>
              </div>

              <div className="legal-section">
                <h3>Machine Learning Models</h3>
                <p>
                  Solar plant performance data may be used to train and improve our machine learning models
                  for predictive maintenance. This data is always anonymized and aggregated before use.
                </p>
              </div>

              <div className="legal-section">
                <h3>Legal Compliance</h3>
                <p>
                  We may process your data to comply with legal obligations, respond to lawful requests
                  from public authorities, and protect our rights and the rights of others.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Data Sharing */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Data Sharing and Disclosure</h2>
            <p>When and with whom we may share your information</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Service Providers</h3>
                <p>
                  We work with trusted third-party service providers who assist us in operating our platform.
                  Our infrastructure is hosted on Render, a SOC 2 Type II certified cloud platform. We also
                  use payment processors and analytics services. These providers are contractually bound to
                  protect your data and may only use it for the specified purposes.
                </p>
              </div>

              <div className="legal-section">
                <h3>Business Transfers</h3>
                <p>
                  In the event of a merger, acquisition, or sale of assets, your information may be transferred
                  as part of that transaction. We will provide notice before your data becomes subject to a
                  different privacy policy.
                </p>
              </div>

              <div className="legal-section">
                <h3>Legal Requirements</h3>
                <p>
                  We may disclose your information when required by law, such as in response to a court order,
                  subpoena, or other legal process, or when we believe disclosure is necessary to protect our
                  rights or the safety of others.
                </p>
              </div>

              <div className="legal-section">
                <h3>With Your Consent</h3>
                <p>
                  We may share your information with third parties when you have given us explicit consent
                  to do so, such as when integrating with third-party systems or services.
                </p>
              </div>

              <div className="legal-highlight">
                <strong>We never sell your personal data.</strong> Your information is not for sale to
                advertisers, data brokers, or any other third parties.
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Your Rights */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Your Rights Under GDPR</h2>
            <p>You have control over your personal data</p>
          </ScrollReveal>

          <div className="comparison-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            {userRights.map((item, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <motion.div className="comparison-card" whileHover={{ y: -5 }}>
                  <span className="cc-value" style={{ fontSize: '1.25rem' }}>Right to {item.right}</span>
                  <span className="cc-label">{item.description}</span>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.3}>
            <div className="legal-content" style={{ marginTop: 'var(--space-2xl)' }}>
              <p>
                To exercise any of these rights, please contact our Data Protection Officer at{' '}
                <a href="mailto:privacy@ai4solar.com" style={{ color: 'var(--color-energy)' }}>
                  privacy@ai4solar.com
                </a>
                . We will respond to your request within 30 days.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Data Security */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Data Security</h2>
            <p>How we protect your information</p>
          </ScrollReveal>

          <div className="mission-grid">
            <ScrollReveal className="mission-content">
              <p className="mission-lead">
                We implement industry-leading security measures to protect your data from unauthorized
                access, disclosure, alteration, and destruction.
              </p>
              <ul className="security-list">
                {securityMeasures.map((measure, i) => (
                  <li key={i} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 'var(--space-sm)',
                    marginBottom: 'var(--space-sm)',
                    color: 'var(--color-gray-300)'
                  }}>
                    <span style={{ color: 'var(--color-energy)', width: '20px', height: '20px', flexShrink: 0, marginTop: '2px' }}>
                      {Icons.check}
                    </span>
                    {measure}
                  </li>
                ))}
              </ul>
            </ScrollReveal>

            <ScrollReveal delay={0.2} className="mission-visual">
              <motion.div
                className="security-badge"
                whileHover={{ scale: 1.02 }}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  borderRadius: 'var(--radius-xl)',
                  padding: 'var(--space-2xl)',
                  textAlign: 'center'
                }}
              >
                <span style={{ width: '64px', height: '64px', margin: '0 auto var(--space-lg)', display: 'block', color: 'var(--color-energy)' }}>
                  {Icons.shield}
                </span>
                <h3 style={{ color: 'var(--color-white)', marginBottom: 'var(--space-sm)' }}>SOC 2 Certified</h3>
                <p style={{ color: 'var(--color-gray-400)', fontSize: '0.875rem' }}>
                  Your solar data is hosted on SOC 2 Type II certified infrastructure with enterprise-grade security controls.
                </p>
              </motion.div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Cookies */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Cookies and Tracking</h2>
            <p>How we use cookies on our platform</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Essential Cookies</h3>
                <p>
                  Required for the platform to function properly, including authentication, security,
                  and remembering your preferences. These cannot be disabled.
                </p>
              </div>

              <div className="legal-section">
                <h3>Analytics Cookies</h3>
                <p>
                  Help us understand how visitors interact with our website and platform, allowing us
                  to improve user experience. You can opt out of these cookies.
                </p>
              </div>

              <div className="legal-section">
                <h3>Marketing Cookies</h3>
                <p>
                  Used to deliver relevant advertisements and measure the effectiveness of our marketing
                  campaigns. These are only set with your explicit consent.
                </p>
              </div>

              <p style={{ marginTop: 'var(--space-lg)' }}>
                For more details, please see our{' '}
                <a href="/cookies" style={{ color: 'var(--color-energy)' }}>Cookie Policy</a>.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Data Retention */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Data Retention</h2>
            <p>How long we keep your information</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <div className="legal-section">
                <h3>Active Accounts</h3>
                <p>
                  We retain your personal data and solar plant data for as long as your account is active
                  and you continue to use our services.
                </p>
              </div>

              <div className="legal-section">
                <h3>After Account Closure</h3>
                <p>
                  When you close your account, we will delete or anonymize your personal data within 90 days,
                  except where we are required to retain it for legal, regulatory, or legitimate business purposes.
                </p>
              </div>

              <div className="legal-section">
                <h3>Historical Performance Data</h3>
                <p>
                  Anonymized and aggregated solar performance data may be retained indefinitely to improve
                  our machine learning models and provide industry benchmarks.
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* International Transfers */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>International Data Transfers</h2>
            <p>Cross-border data protection</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                AI4Renewables is headquartered in the European Union. Our platform infrastructure is hosted
                on Render, which operates data centers in the United States. For transfers of personal data
                from the EU to the US, we rely on the following safeguards:
              </p>
              <ul style={{ marginTop: 'var(--space-lg)', marginLeft: 'var(--space-lg)' }}>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  EU-US Data Privacy Framework certification of our service providers
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Standard Contractual Clauses approved by the European Commission
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Technical and organizational measures to protect data in transit and at rest
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  SOC 2 Type II certified infrastructure (Render)
                </li>
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Children's Privacy */}
      <section className="about-section about-dark">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Children's Privacy</h2>
            <p>Our platform is not intended for children</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                The AI4Solar platform is designed for business use and is not intended for individuals
                under the age of 16. We do not knowingly collect personal information from children.
                If we become aware that we have inadvertently collected data from a child, we will
                take steps to delete it promptly.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Changes to Policy */}
      <section className="about-section">
        <div className="container">
          <ScrollReveal className="section-header">
            <h2>Changes to This Policy</h2>
            <p>How we will notify you of updates</p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="legal-content">
              <p>
                We may update this Privacy Policy from time to time to reflect changes in our practices
                or applicable laws. When we make material changes, we will:
              </p>
              <ul style={{ marginTop: 'var(--space-lg)', marginLeft: 'var(--space-lg)' }}>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Update the "Last updated" date at the top of this policy
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Notify you via email or through a prominent notice on our platform
                </li>
                <li style={{ marginBottom: 'var(--space-sm)', color: 'var(--color-gray-300)' }}>
                  Give you the opportunity to review the changes before they take effect
                </li>
              </ul>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Contact */}
      <section className="draft-cta">
        <div className="container">
          <ScrollReveal className="draft-cta-content">
            <h2>Questions About Privacy?</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices,
              please don't hesitate to contact our Data Protection Officer.
            </p>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-md)',
              marginBottom: 'var(--space-xl)'
            }}>
              <a
                href="mailto:privacy@ai4solar.com"
                style={{
                  color: 'var(--color-energy)',
                  fontSize: '1.125rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-sm)'
                }}
              >
                <span style={{ width: '20px', height: '20px' }}>{Icons.mail}</span>
                privacy@ai4solar.com
              </a>
            </div>
            <div className="cta-buttons">
              <GlowButton href="/contact" variant="solar" size="large">
                Contact Us
              </GlowButton>
              <GlowButton href="/terms" variant="outline" size="large">
                Terms of Service
              </GlowButton>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  )
}
